"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Eye,
  Layers,
  Network,
  Target,
  Compass,
  Search,
  Activity,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function PerceptionManagementPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · Perception Management"
        overlineClassName="text-red"
        title={
          <>
            The Systematic Engineering of <span className="text-gradient-premium">How Entities Are Perceived</span>
          </>
        }
        subtitle="The systematic engineering of how leaders, governments, corporations, and institutions are perceived across every digital surface, news cycle, and public conversation. Not public relations. Not reputation repair. The intelligence-driven architecture of narrative control."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "Perception Management" }]}
        badge="Definition · Pillar 1 of the Penta P's"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Sparkles className="size-3.5 text-red" />
            <span className="text-overline text-red">Featured Snippet Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What perception management is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">Perception management</span> is the organized effort to engineer how an entity is perceived by its target audiences across every digital and public surface. It encompasses intelligence gathering, narrative design, content creation, multi-platform deployment, crisis response, and continuous monitoring — operating as an integrated system to ensure that public perception aligns with strategic intent.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Perception management operates on a foundational principle: the audience is king. The public decides what to view and how to interpret it. The role of perception management is not to fabricate reality but to ensure that authentic truths are presented, amplified, and defended across every channel with maximum impact and consistency.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            At sovereign scale, perception management involves managing the present, reconstructing the past, and idealizing the future. It is architectural, not reactive. The narrative is designed before deployment. The defense is built before the attack. The intelligence is gathered before the strategy is formed.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Layers className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Four Components</span>
          </div>
          <h2 className="text-display-md mb-4">The interdependent components.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Perception management at its highest operational level rests on four interdependent components — each essential, none sufficient alone.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Search, title: "Intelligence", body: "Before any narrative is deployed, the landscape is interrogated. The intelligence platform monitors 200+ social platforms and 100,000+ news sources across 50+ languages in real time. Five dimensions of intelligence are derived from raw data. 89% prediction accuracy.", accent: "purple" },
            { icon: Compass, title: "Strategy", body: "The Penta P's framework transforms intelligence into an operational plan. Strategy is not a document — it is an operational blueprint specifying which platforms activate, which teams deploy, which audiences receive which messages, and which threats to preemptively counter.", accent: "cyan" },
            { icon: Eye, title: "Deployment", body: "Content deploys across 1,000+ distribution points with platform-specific optimization. Every piece of content is adapted through nine vectors — platform norms, algorithm preferences, audience psychographics, cultural context, regional dialect, age-appropriate messaging, and behavioral pattern analysis.", accent: "red" },
            { icon: Activity, title: "Monitoring", body: "The loop never closes — it accelerates. The intelligence and command platforms feed continuous sentiment tracking and outcome measurement back into the intelligence stage. Early warning systems detect negative trends before they become crises. Every cycle is smarter than the last.", accent: "amber-gold" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Target className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Five Dimensions</span>
          </div>
          <h2 className="text-display-md mb-4">How perception is engineered across every axis.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Perception is not a single variable. It is a five-dimensional structure that must be engineered simultaneously across every axis.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { dim: "Reality Framework", body: "Establishes the existing belief structures, cultural contexts, and cognitive frameworks that shape how target audiences interpret new information. Before any narrative is deployed, the perceptual landscape is mapped.", accent: "red" },
            { dim: "Narrative Trajectory", body: "Engineers the trajectory of perception over time. Sequential narrative deployment builds cumulative understanding — projecting where perception will be in three months, six months, and a year.", accent: "purple" },
            { dim: "Perception Anchors", body: "Creates stable reference points that stabilize desired perceptions against attack. Irrefutable truths, verified achievements, and authenticated endorsements become the anchors.", accent: "cyan" },
            { dim: "Consistency Protocols", body: "Maintains consistent brand perception across every platform, every piece of content, and every interaction. Information is ensured to be favorable to client motive across every channel — through systematic consistency, not censorship.", accent: "emerald" },
            { dim: "Reinforcement Loops", body: "Builds systems where positive perception generates more positive perception. Satisfied stakeholders become advocates. Verified achievements attract recognition. Authentic goodwill compounds organically.", accent: "amber-gold" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.dim}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{p.dim}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model that includes Perception as the first pillar.", accent: "cyan" },
            { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", desc: "The Perception-pillar discipline for converting emergencies to advantage.", accent: "coral" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline perception management supports.", accent: "red" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The methodology that operationalizes perception management.", accent: "emerald" },
            { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", desc: "The organizational form that executes perception management.", accent: "purple" },
            { term: "eGovernance", href: "/glossary/egovernance/", desc: "The Policy pillar of the same framework.", accent: "lime" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Perception Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">Five operational pillars of perception.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Perception management is engineered through five operational pillars. Each pillar is a structural component — the architecture fails if any pillar is missing or weak.
          </p>
        </div>
        <PillarGridBlock
          pillars={[
            { title: "Intelligence Foundation", body: "The 200+ platform monitoring, 100,000+ news source ingestion, and 1,000+ dark web feed analysis. The intelligence foundation provides the raw material that every other pillar transforms.", accent: "red" },
            { title: "Narrative Architecture", body: "Sequential narrative deployment that builds cumulative understanding. Projects where perception will be in three months, six months, and a year. The architecture is designed before the narrative is deployed.", accent: "purple" },
            { title: "Authentic Deployment", body: "95-98% organic authenticity rating through the Organic Simulation Engine, Narrative Generation Engine, Cognitive Resonance Engine, and Echo Chamber Architecture. Authentic at population scale.", accent: "cyan" },
            { title: "Consistency Enforcement", body: "Information architecture ensures favorable perception across every channel. Not through censorship but through systematic consistency, coordinated deployment, and disciplined content engineering.", accent: "emerald" },
            { title: "Reinforcement Loops", body: "Systems where positive perception generates more positive perception. Satisfied stakeholders become advocates. Verified achievements attract recognition. The compounding effect is what makes perception management an operating system.", accent: "amber-gold" },
            { title: "Defensive Architecture", body: "Perception anchors — irrefutable truths, verified achievements, authenticated endorsements — that stabilize desired perceptions against attack. The defense is built before the attack materializes.", accent: "denim" },
          ]}
          variant="card"
          columns={3}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">Perception management in practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of perception management applied across different entity types. Each example shows the methodology producing engineered outcomes.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Sovereign Leader Positioning", body: "A national leader preparing for a re-election campaign. The perception architecture is built months in advance. Sequential narrative deployment. Anchoring verified achievements. Defensive architecture against anticipated attacks.", accent: "red" },
            { title: "Corporate Brand Defense", body: "A Fortune 500 corporation under sustained narrative attack. The perception management methodology engages with 95-98% organic authenticity rating. The counter-narrative deploys across 50+ platforms. The brand emerges stronger.", accent: "purple" },
            { title: "Royal Family Reputation", body: "A royal family managing sustained public attention. Perception management is the operating discipline. The methodology maintains the family's reputation across cultural contexts and generational transitions.", accent: "cyan" },
            { title: "Public Figure Positioning", body: "A high-net-worth public figure building a sovereign-grade public presence. The perception architecture is engineered for sustained impact. The methodology operates with the same discipline as sovereign-scale engagements.", accent: "emerald" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <BookOpen className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What perception management is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions that conflate perception management with public relations, propaganda, or media manipulation. The distinctions are ethical and operational.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is perception management the same as public relations?", a: "No. Public relations focuses on media relationships and message distribution. Perception management focuses on engineering the perceptual environment — the information architecture, the narrative trajectory, and the audience cognition. PR is an instrument; perception management is the discipline." },
            { q: "Is perception management propaganda?", a: "No. Propaganda typically involves fabrication and deception. Perception management operates on authentic truths, presented and amplified with maximum impact. The role of perception management is not to fabricate reality but to ensure that authentic truths reach audiences with full effect." },
            { q: "Does perception management involve media manipulation?", a: "No. Perception management works within the information environment. It does not manipulate media organizations, journalists, or editorial processes. The methodology operates through content deployment, narrative architecture, and authentic audience engagement." },
            { q: "Is perception management the same as marketing?", a: "No. Marketing promotes products and services. Perception management engineers how an entity is perceived across every dimension — operational, political, reputational, institutional. The scope is broader. The discipline is deeper. The outcomes are more strategic." },
            { q: "Can perception management be applied to crisis situations?", a: "Yes. Crisis transformation is the perception management discipline applied to emergency contexts. The Golden Hour Principle, the containment-reconstruction-elevation framework, and the 500+ pre-built playbooks are all instruments of the perception management methodology under pressure." },
            { q: "Does perception management produce ethical outcomes?", a: "When operated within the integrity framework, perception management produces ethical outcomes. The methodology amplifies authentic truth, supports legitimate institutions, and protects entities under attack. Misuse of the methodology is possible — as with any powerful operational discipline. The framework is engineered for legitimate sovereign-scale applications." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Eye className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage perception management.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Perception management is available through sovereign-scale engagements — where the response speed, the multi-platform depth, and the integration with the broader Penta P's framework match the stakes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/glossary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                Glossary Hub
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "Crisis Transformation", description: "The Perception-pillar discipline under pressure.", href: "/glossary/crisis-transformation/", icon: BookOpen, accent: "coral" },
          { title: "Penta P's Framework", description: "The five-domain model that includes Perception.", href: "/glossary/penta-ps-framework/", icon: Network, accent: "cyan" },
        ]}
      />
    </>
  );
}
